import React, { useState, useRef } from 'react';
import { playCorrect, playWrong, playClap } from '../../utils/sounds';
import LoginPromptModal from '../../components/LoginPromptModal';
import { useAuth } from '../../contexts/AuthContext';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY    = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR      = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const FONT       = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40%       { transform: scale(1.15) translateY(-6px); }
  70%       { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50%       { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50%       { transform: translateY(-8px) rotate(-3deg); }
`;
const pulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 4px 20px rgba(0,0,0,0.12); }
  50%       { transform: scale(1.04); box-shadow: 0 8px 28px rgba(0,0,0,0.18); }
`;

// ─── Intro Styled Components ──────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR};
  display: flex; flex-direction: column; align-items: center; overflow: hidden;
  font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px;
  display: flex; align-items: center; gap: 10px;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px;
  background: transparent; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatingBall = styled.div<{ $color: string; $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 52px; height: 52px; border-radius: 50%;
  background: ${p => p.$color};
  box-shadow: 0 4px 14px rgba(0,0,0,0.2);
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
`;
const CharCard = styled.div`
  width: min(60vw, 220px); aspect-ratio: 1;
  display: flex; align-items: center; justify-content: center;
  font-size: clamp(72px, 20vw, 110px);
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease;
  font-family: ${FONT};
`;
const Handle = styled.div`
  width: 40px; height: 4px; background: #e0e0e0;
  border-radius: 99px; margin: 0 auto 18px;
`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900;
  margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`
  font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 20px;
`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff;
  font-size: 17px; font-weight: 800; letter-spacing: 0.5px;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4);
  font-family: ${FONT}; transition: all 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(43,188,175,0.5); }
  &:active { transform: translateY(0); }
`;

// ─── Game Styled Components ───────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px;
  display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`
  font-size: 17px; font-weight: 800; color: #1a2a2a;
`;
const ScoreBadge = styled.div`
  background: rgba(45,188,175,0.12); color: ${PRIMARY};
  padding: 6px 14px; border-radius: 10px;
  font-size: 14px; font-weight: 800; font-family: ${FONT};
`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; padding: 24px 16px;
  gap: 20px; max-width: 480px; margin: 0 auto; width: 100%;
`;
const TargetCard = styled.div`
  background: #fff; border-radius: 24px;
  padding: 28px 32px; text-align: center;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  width: 100%;
`;
const TargetLabel = styled.div`
  font-size: 13px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 8px;
`;
const TargetName = styled.div`
  font-size: clamp(32px, 10vw, 48px); font-weight: 900;
  color: #1a2a2a; line-height: 1.1;
`;
const RoundInfo = styled.div`
  font-size: 13px; color: #7a8a8a; margin-top: 6px;
`;
const ColorGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 12px; width: 100%;
`;
const ColorBtn = styled.button<{ $color: string; $feedback?: 'correct' | 'incorrect' | null }>`
  height: 100px; border-radius: 20px; border: 3px solid;
  border-color: ${p => p.$feedback === 'correct' ? '#22c55e' : p.$feedback === 'incorrect' ? '#ef4444' : 'transparent'};
  background: ${p => p.$color};
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  ${p => p.$feedback === 'correct' ? css`animation: ${pulse} 0.6s ease;` : ''}
  &:hover { transform: translateY(-3px) scale(1.03); box-shadow: 0 8px 28px rgba(0,0,0,0.2); }
  &:active { transform: scale(0.97); }
`;
const FeedbackMsg = styled.div<{ $type: 'correct' | 'incorrect' }>`
  background: ${p => p.$type === 'correct' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)'};
  color: ${p => p.$type === 'correct' ? '#16a34a' : '#dc2626'};
  border-radius: 12px; padding: 10px 20px;
  font-size: 15px; font-weight: 700;
  animation: ${fadeInScale} 0.3s ease;
`;
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
`;
const Modal = styled.div`
  background: #fff; border-radius: 28px; padding: 28px 24px 24px;
  text-align: center; max-width: 300px; width: 88%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  animation: ${fadeInScale} 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
  font-family: ${FONT};
`;
const ModalEmoji = styled.div`font-size: 52px; margin-bottom: 10px;`;
const ModalTitle = styled.div`font-size: 24px; font-weight: 900; color: #1a2a2a; margin-bottom: 6px;`;
const ModalDesc = styled.div`font-size: 14px; color: #7a8a8a; margin-bottom: 20px; line-height: 1.5;`;
const ModalBtnRow = styled.div`display: flex; gap: 8px; justify-content: center;`;
const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 11px 22px; border-radius: 99px;
  border: ${p => p.$primary ? 'none' : '2px solid #e0e0e0'};
  background: ${p => p.$primary ? PRIMARY_GR : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#555'};
  font-size: 14px; font-weight: 700; cursor: pointer;
  font-family: ${FONT};
  box-shadow: ${p => p.$primary ? '0 4px 16px rgba(43,188,175,0.4)' : 'none'};
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
interface ColorType { name: string; hex: string; }

const COLORS: ColorType[] = [
  { name: 'Merah',   hex: '#ef4444' },
  { name: 'Biru',    hex: '#3b82f6' },
  { name: 'Hijau',   hex: '#22c55e' },
  { name: 'Kuning',  hex: '#facc15' },
  { name: 'Ungu',    hex: '#a855f7' },
  { name: 'Oranye',  hex: '#f97316' },
];
const MAX_ROUNDS = 10;

function speak(text: string) {
  try {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'id-ID'; u.rate = 0.8;
    const voices = window.speechSynthesis.getVoices();
    const id = voices.find(v => v.lang.startsWith('id'));
    if (id) u.voice = id;
    window.speechSynthesis.speak(u);
  } catch (_) {}
}

function pickRound() {
  const correct = COLORS[Math.floor(Math.random() * COLORS.length)];
  const wrong = COLORS.filter(c => c.name !== correct.name).sort(() => Math.random() - 0.5).slice(0, 3);
  return { correct, options: [correct, ...wrong].sort(() => Math.random() - 0.5) };
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ColorGame() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [showIntro, setShowIntro] = useState(true);
  const [target, setTarget]       = useState<ColorType | null>(null);
  const [options, setOptions]     = useState<ColorType[]>([]);
  const [score, setScore]         = useState(0);
  const [round, setRound]         = useState(1);
  const [feedback, setFeedback]   = useState<{ color: string; type: 'correct' | 'incorrect' } | null>(null);
  const [gameOver, setGameOver]   = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const locked = useRef(false); // synchronous lock, prevents rapid double-click bypass

  const startGame = () => {
    const { correct, options: opts } = pickRound();
    locked.current = false;
    setScore(0); setRound(1); setGameOver(false);
    setShowLoginPrompt(false); setFeedback(null);
    setTarget(correct); setOptions(opts);
    setTimeout(() => speak(correct.name), 400);
  };

  const handleClick = (color: ColorType) => {
    if (locked.current || !target) return;
    locked.current = true; // lock synchronously — prevents rapid double-click
    const isCorrect = color.name === target.name;
    setFeedback({ color: color.name, type: isCorrect ? 'correct' : 'incorrect' });
    if (isCorrect) {
      playCorrect();
      setScore(s => s + 10);
      if (round >= MAX_ROUNDS) {
        setTimeout(() => {
          setGameOver(true);
          playClap();
        }, 800);
      } else {
        const nextRound = round + 1;
        const { correct: nextCorrect, options: nextOpts } = pickRound();
        setTimeout(() => {
          setFeedback(null);
          setRound(nextRound);
          setTarget(nextCorrect);
          setOptions(nextOpts);
          locked.current = false; // unlock for new round
          setTimeout(() => speak(nextCorrect.name), 300);
          if (!user && !authLoading && nextRound === 4) {
            setTimeout(() => setShowLoginPrompt(true), 600);
          }
        }, 900);
      }
    } else {
      playWrong();
      // Wrong answer → auto-advance to next round after feedback
      if (round >= MAX_ROUNDS) {
        setTimeout(() => {
          setGameOver(true);
        }, 900);
      } else {
        const nextRound = round + 1;
        const { correct: nextCorrect, options: nextOpts } = pickRound();
        setTimeout(() => {
          setFeedback(null);
          setRound(nextRound);
          setTarget(nextCorrect);
          setOptions(nextOpts);
          locked.current = false;
          setTimeout(() => speak(nextCorrect.name), 300);
          if (!user && !authLoading && nextRound === 4) {
            setTimeout(() => setShowLoginPrompt(true), 600);
          }
        }, 900);
      }
    }
  };

  if (showIntro) {
    return (
      <IntroOverlay>
        <IntroWrapper>
        <TopNav>
          <BackBtn onClick={() => navigate('/')}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
        </TopNav>
        <CharArea>
          <FloatingBall $color="#ef4444" $top="18%" $left="8%" $delay={0} />
          <FloatingBall $color="#3b82f6" $top="14%" $right="10%" $delay={1} />
          <FloatingBall $color="#22c55e" $bottom="20%" $left="10%" $delay={2} />
          <FloatingBall $color="#facc15" $bottom="18%" $right="8%" $delay={3} />
          <CharCard>🎨</CharCard>
        </CharArea>
        <InfoCard>
          <Handle />
          <IntroTitle>Tebak <span>Warna!</span></IntroTitle>
          <IntroDesc>Dengarkan nama warna yang disebutkan, lalu pilih warna yang tepat. Cepat dan tepat!</IntroDesc>
          <StartBtn onClick={() => { setShowIntro(false); startGame(); }}>Mulai Bermain</StartBtn>
        </InfoCard>
        </IntroWrapper>
      </IntroOverlay>
    );
  }

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => setShowIntro(true)}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
          <GameTitle>Tebak Warna</GameTitle>
        </NavLeft>
        <ScoreBadge>Skor: {score}</ScoreBadge>
      </GameNav>

      <GameBody>
        <TargetCard>
          <TargetLabel>Cari Warna</TargetLabel>
          <TargetName>{target?.name}</TargetName>
          <RoundInfo>Ronde {round} / {MAX_ROUNDS}</RoundInfo>
        </TargetCard>

        {feedback && (
          <FeedbackMsg $type={feedback.type}>
            {feedback.type === 'correct' ? '✅ Benar! +10' : '❌ Salah! Coba lagi'}
          </FeedbackMsg>
        )}

        <ColorGrid>
          {options.map((c) => (
            <ColorBtn
              key={c.name}
              $color={c.hex}
              $feedback={feedback?.color === c.name ? feedback.type : null}
              onClick={() => handleClick(c)}
            />
          ))}
        </ColorGrid>
      </GameBody>

      {gameOver && (
        <ModalOverlay>
          <Modal>
            <ModalEmoji>🎉</ModalEmoji>
            <ModalTitle>Selesai!</ModalTitle>
            <ModalDesc>Skor akhir kamu: <strong>{score}</strong> dari {MAX_ROUNDS * 10}</ModalDesc>
            <ModalBtnRow>
              <ModalBtn onClick={() => navigate("/games")}>Menu</ModalBtn>
              <ModalBtn $primary onClick={() => { setGameOver(false); startGame(); }}>Main Lagi</ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}

      {showLoginPrompt && !user && !authLoading && (
        <LoginPromptModal
          
          onClose={() => setShowLoginPrompt(false)}
          subtitle="Silahkan login terlebih dahulu untuk melanjutkan permainan."
          required
        />
      )}
    </GameWrap>
  );
}
